import { useMemo } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { PageBoundingBox } from '../../../../../../../../backend/src/matter/fact/fact-bounding-box';
import { useGetProceduralFile } from '@/api/queries/useGetProceduralFile';
import { Icon } from '@/new/components/icon';
import { PDFViewer } from '@/new/components/pdf-viewer';
import { Button } from '@/new/components/shadcn/button';
import { BoundingBoxWithId } from '@/types';

export interface ProceduralSourceRef {
  fileId: string;
  fileName: string;
  pageNumber: number;
  chunkId: string;
  content: string;
  boundingBox: PageBoundingBox;
}

interface ProceduralSourcePanelProps {
  source: ProceduralSourceRef | null;
  onClose: () => void;
}

const PANEL_WIDTH_PX = 560;

/**
 * Inline source panel rendered as a flex sibling to the timeline. Mirrors the
 * Kim ReferenceSidePanel approach: the panel is positioned in flow (not fixed)
 * so the timeline column shrinks when it opens, and the PDF viewer fits the
 * available height instead of stacking under a page-level scroll container.
 */
export function ProceduralSourcePanel({ source, onClose }: ProceduralSourcePanelProps) {
  return (
    <AnimatePresence initial={false}>
      {source && (
        <motion.div
          key="procedural-source-panel"
          initial={{ width: 0 }}
          animate={{ width: PANEL_WIDTH_PX }}
          exit={{ width: 0 }}
          transition={{ type: 'tween', duration: 0.2 }}
          className="relative h-full shrink-0 overflow-hidden"
        >
          <div
            className="absolute top-0 right-0 flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
            style={{ width: PANEL_WIDTH_PX }}
          >
            <ProceduralSourcePanelBody source={source} onClose={onClose} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ProceduralSourcePanelBody({ source, onClose }: { source: ProceduralSourceRef; onClose: () => void }) {
  const { matterId } = useParams();
  const { data: file, isLoading, error } = useGetProceduralFile(matterId, source.fileId);

  const highlight: BoundingBoxWithId = useMemo(
    () => ({ ...source.boundingBox, id: source.chunkId }),
    [source.boundingBox, source.chunkId],
  );

  const fileMetadata = useMemo(
    () => (file ? { name: file.name, extension: file.extension, pageDimensions: file.pageDimensions } : undefined),
    [file],
  );

  return (
    <>
      <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-3">
        <Icon className="lucide--file-text mt-0.5 shrink-0 text-gray-500" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{source.fileName}</p>
          <p className="text-xs text-gray-500">Page {source.pageNumber}</p>
        </div>
        <Button onClick={onClose} variant="ghost" size="iconSm" aria-label="Close source panel">
          <Icon className="lucide--x" />
        </Button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {error && (
          <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-500">
            Failed to load the source document.
          </div>
        )}
        {!error && (isLoading || !file) && (
          <div className="flex h-full items-center justify-center">
            <Icon className="lucide--loader-2 animate-spin text-gray-400" />
          </div>
        )}
        {file && (
          <PDFViewer
            docId={file.id}
            fileUrl={file.url}
            fileMetadata={fileMetadata}
            highlights={[highlight]}
            highlightedFactId={source.chunkId}
            hideFeatureButtons={true}
            hideSnippingTool={true}
            hideDownload={true}
            fileName={file.name}
          />
        )}
      </div>
    </>
  );
}
